<template>
    <div :class="surface === 'plan' ? 'px-4 py-3' : ''">
        <div v-if="surface === 'plan'" class="flex items-start gap-2">
            <UIcon name="i-heroicons-shield-check" class="mt-0.5 size-4 shrink-0 text-slate-500" />
            <div class="min-w-0 flex-1">
                <div class="flex items-center justify-between gap-3">
                    <div>
                        <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-900">Do-Not-Contact</h4>
                        <p class="mt-1 text-xs text-slate-600" role="status">{{ policyStatus }}</p>
                    </div>
                    <UButton color="neutral" variant="ghost" size="xs" trailing-icon="i-heroicons-chevron-right" :disabled="loading || saving || finding" @click="openEditor">
                        Review rules
                    </UButton>
                </div>

            </div>
        </div>
        <div v-else>
            <UButton color="neutral" variant="ghost" size="xs" icon="i-heroicons-shield-check" :disabled="loading || saving || finding" @click="openEditor">
                Do-Not-Contact
                <span class="text-[10px] font-normal text-slate-500">{{ policyStatus }}</span>
            </UButton>

        </div>

        <UModal v-model:open="open" :title="reviewing ? 'Review exclusion changes' : 'Do-Not-Contact'"
            :dismissible="!saving && !finding && !companyExclusionsBusy" :close="!saving && !finding && !companyExclusionsBusy" description="Campaign-specific exclusions and exceptions. Organization rules are unchanged." :ui="{ content: 'sm:max-w-3xl', footer: 'justify-between' }">
            <template #body>
                <div v-if="loading" role="status" class="text-sm text-slate-500">Loading saved rules…</div>
                <div v-else-if="!state" class="space-y-3">
                    <p role="alert" class="text-sm text-red-700">{{ error }}</p>
                    <UButton color="neutral" variant="outline" @click="loadPolicy">Retry loading</UButton>
                </div>
                <div v-else-if="draft" class="space-y-5">
                    <p v-if="error" role="alert" class="text-sm text-red-700">{{ error }}</p>
                    <div v-if="saveUncertain" class="space-y-2">
                        <p class="text-xs text-slate-600">Reload the saved rules before making another change. Your previous request may have reached the server.</p>
                        <UButton color="neutral" variant="outline" @click="loadPolicy">Reload saved rules</UButton>
                    </div>
                    <p v-if="notice" role="status" class="text-sm text-green-800">{{ notice }}</p>
                    <div v-if="discoveryError" class="space-y-2 rounded border border-amber-200 bg-amber-50 p-3">
                        <p role="alert" class="text-sm text-amber-900">Your rules were saved, but finding contacts failed: {{ discoveryError }}</p>
                        <UButton v-if="state.canSaveAndFind" :loading="finding" :disabled="saving" @click="retryDiscovery">Retry finding contacts</UButton>
                    </div>
                    <div class="flex items-start gap-3 rounded-lg bg-slate-50 px-4 py-3">
                        <UIcon name="i-heroicons-adjustments-horizontal" class="mt-0.5 size-4 shrink-0 text-slate-500" />
                        <div>
                            <p class="text-sm font-medium text-slate-800">{{ reviewing ? 'Confirm changes to this campaign' : 'Start with default rules. Make exceptions deliberately.' }}</p>
                            <p class="mt-1 text-xs leading-relaxed text-slate-500">
                                Saving changes eligibility for this campaign only. It does not approve contacts, send messages, resume a paused campaign, or change organization exclusions.
                            </p>
                        </div>
                    </div>

                    <p v-if="!state.canSave" class="text-sm text-amber-800">Pause this campaign before changing rules. Active campaigns must use manual review to edit these settings.</p>
                    <p v-if="!state.canEditCompanyRules" class="text-xs text-slate-500">Only organization admins can change company exceptions.</p>
                    <template v-if="!reviewing">
                        <div class="overflow-hidden rounded-lg border border-slate-200">
                            <div class="grid grid-cols-[1fr_auto] gap-4 border-b border-slate-200 bg-slate-50/70 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                <span>Exclusion rule</span>
                                <span>Campaign rule</span>
                            </div>
                            <div class="divide-y divide-slate-100">
                                <div v-for="rule in rules" :key="rule.key" class="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                                    <div class="flex min-w-0 items-start gap-3">
                                        <UIcon :name="rule.icon" class="mt-0.5 size-4 shrink-0 text-slate-400" />
                                        <div>
                                            <p class="text-sm font-medium text-slate-800">{{ rule.label }}</p>
                                            <p class="mt-1 max-w-sm text-xs leading-relaxed text-slate-500">{{ rule.description }}</p>
                                        </div>
                                    </div>
                                    <div class="w-44 shrink-0">
                                        <USelect v-model="draft[rule.key]" :items="choices" :disabled="!canEdit(rule.key) || saving || finding || saveUncertain"
                                            :aria-label="`${rule.label} rule`" class="w-full" size="sm" />
                                        <p v-if="rule.key === 'previousReplies'" class="mt-1 text-[10px] text-slate-400">Always respected</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <details class="group rounded-lg border border-slate-200 px-4 py-3 text-xs">
                            <summary class="cursor-pointer font-medium text-slate-600">What does an exception cover?</summary>
                            <div class="mt-3 space-y-2 leading-relaxed text-slate-500">
                                <p>A company exception covers every company in that category, including future matches. It does not pick individual companies.</p>
                                <p>Manual company lists and CRM company exclusions are separate categories. Allowing one does not allow the other.</p>
                                <p>Individual-contact do-not-contact rules are always respected.</p>
                                <p>Active outreach, same-sequence checks, previous replies, and campaign-specific exclusions stay separate. These controls do not change them.</p>
                            </div>
                        </details>
                    </template>

                    <template v-else>
                        <div class="overflow-hidden rounded-lg border border-slate-200">
                            <div class="border-b border-slate-200 bg-slate-50/70 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                {{ changes.length }} {{ changes.length === 1 ? 'change' : 'changes' }} to this campaign
                            </div>
                            <div class="divide-y divide-slate-100">
                                <div v-for="change in changes" :key="change.key" class="px-4 py-4">
                                    <div class="flex flex-wrap items-center justify-between gap-2">
                                        <p class="text-sm font-medium text-slate-800">{{ change.label }}</p>
                                        <span class="rounded px-2 py-0.5 text-[10px] font-medium"
                                            :class="change.to === 'allow' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'">
                                            {{ change.to === 'allow' ? 'Add exception' : 'Remove exception' }}
                                        </span>
                                    </div>
                                    <div class="mt-2 flex items-center gap-2 text-xs">
                                        <span class="text-slate-500">{{ choiceLabel(change.from) }}</span>
                                        <UIcon name="i-heroicons-arrow-right" class="size-3 text-slate-400" />
                                        <span class="font-medium text-slate-800">{{ choiceLabel(change.to) }}</span>
                                    </div>
                                    <p class="mt-2 text-xs leading-relaxed text-slate-500">{{ change.to === 'allow' ? change.scope : 'Remove this exception and return to the default rule.' }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="space-y-2 text-xs leading-relaxed text-slate-500">
                            <p>Company exceptions apply to all companies in the selected category, including future matches—not individual companies.</p>
                            <p>Individual-contact do-not-contact rules are always respected. Active outreach, same-sequence checks, previous replies, and campaign-specific exclusions remain separate.</p>
                            <p class="font-medium text-slate-700">Saving alone does not start a new contact search. Existing work will use the updated rules.</p>
                            <p v-if="state.canSaveAndFind">Save and find contacts targets 50 ready contacts in total, not 50 additional contacts. Existing searches may be reused, and fewer contacts may match. Contacts still require review before sending.</p>
                        </div>
                    </template>
                </div>
                <CampaignCompanyExclusions v-if="!reviewing" class="mt-5" :campaign-id="campaignId" :disabled="loading || saving || finding || saveUncertain"
                    @updated="loadPolicy" @busy="companyExclusionsBusy = $event" />
            </template>
            <template #footer>
                <template v-if="state && draft && !loading">
                    <template v-if="reviewing">
                        <UButton color="neutral" variant="ghost" :disabled="saving || finding" @click="reviewing = false">Back to rules</UButton>
                        <div class="flex flex-wrap gap-2">
                            <UButton color="neutral" variant="outline" :loading="saving" :disabled="!state.canSave || saveUncertain || finding" @click="savePolicy('save')">Confirm and save</UButton>
                            <UButton v-if="state.canSaveAndFind" :loading="saving" :disabled="saveUncertain || finding" @click="savePolicy('save-and-find')">Save and find contacts</UButton>
                        </div>
                    </template>
                    <template v-else>
                        <UButton color="neutral" variant="ghost" :disabled="!hasEditableExceptions || saving || finding || saveUncertain || companyExclusionsBusy" @click="resetDefaults">Reset to defaults</UButton>
                        <div class="flex items-center gap-2">
                            <UButton color="neutral" variant="outline" :disabled="saving || finding || companyExclusionsBusy" @click="open = false">Close</UButton>
                            <UButton :disabled="!state.canSave || changes.length === 0 || saveUncertain || finding || companyExclusionsBusy" trailing-icon="i-heroicons-arrow-right" @click="reviewing = true">Review changes</UButton>
                        </div>
                    </template>
                </template>
            </template>
        </UModal>
    </div>
</template>

<script setup lang="ts">
import type { CampaignEligibilityPolicyState, SaveCampaignEligibilityPolicyResult } from '@coldreach/campaigns';
import type { CampaignEligibilityPolicy } from '@coldreach/domains/types';

const props = defineProps<{ campaignId: number; surface: 'plan' | 'pipeline' }>();
type Choice = 'default' | 'allow';
type RuleKey = keyof CampaignEligibilityPolicy | 'previousReplies';
type Draft = Record<RuleKey, Choice>;
const state = ref<CampaignEligibilityPolicyState | null>(null);
const draft = ref<Draft | null>(null);
const open = ref(false);
const reviewing = ref(false);
const loading = ref(true);
const saving = ref(false);
const finding = ref(false);
const companyExclusionsBusy = ref(false);
const saveUncertain = ref(false);
const error = ref('');
const notice = ref('');
const discoveryError = ref('');
const choices: { label: string; value: Choice }[] = [
    { label: 'Respect default', value: 'default' },
    { label: 'Allow exception', value: 'allow' },
];
const rules: { key: RuleKey; label: string; icon: string; description: string; scope: string }[] = [
    { key: 'allowRecentCooldownContacts', label: 'Recent outreach cooldown', icon: 'i-heroicons-clock',
        description: 'Contacts inside the waiting period after recent outreach.',
        scope: 'Allow contacts during cooldown after completion or removal of unfinished approved outreach. Active outreach and same-sequence checks stay separate.' },
    { key: 'previousReplies', label: 'Previous replies', icon: 'i-heroicons-chat-bubble-left-right',
        description: 'Contacts who have replied to earlier outreach. This rule cannot be bypassed.', scope: '' },
    { key: 'allowManuallyExcludedCompanies', label: 'Manual company list', icon: 'i-heroicons-building-office-2',
        description: 'Companies on a manually managed exclusion list.',
        scope: 'Allow all companies excluded by the manual list, including future matches. CRM exclusions stay separate.' },
    { key: 'allowCrmDealCompanies', label: 'CRM companies', icon: 'i-heroicons-circle-stack',
        description: 'Companies linked to non-ignored imported CRM deals, at any stage.',
        scope: 'Allow all companies excluded by imported CRM deals, including future matches. The manual list stays separate.' },
];
function toDraft(policy: CampaignEligibilityPolicy): Draft {
    return {
        allowRecentCooldownContacts: policy.allowRecentCooldownContacts ? 'allow' : 'default',
        allowManuallyExcludedCompanies: policy.allowManuallyExcludedCompanies ? 'allow' : 'default',
        allowCrmDealCompanies: policy.allowCrmDealCompanies ? 'allow' : 'default',
        previousReplies: 'default',
    };
}
const policyStatus = computed(() => loading.value ? 'Loading saved rules…' : !state.value
    ? 'Saved rules unavailable' : Object.values(state.value.policy).some(Boolean) ? 'Custom rules' : 'Default rules');
function canEdit(key: RuleKey): boolean {
    return !!state.value?.canSave && key !== 'previousReplies'
        && (key === 'allowRecentCooldownContacts' || state.value.canEditCompanyRules);
}
const hasEditableExceptions = computed(() => rules.some(rule => canEdit(rule.key) && draft.value?.[rule.key] === 'allow'));
const changes = computed(() => {
    if (!state.value || !draft.value) return [];
    const saved = toDraft(state.value.policy);
    return rules.filter(rule => draft.value?.[rule.key] !== saved[rule.key])
        .map(rule => ({ ...rule, from: saved[rule.key], to: draft.value![rule.key] }));
});
function choiceLabel(choice: Choice): string { return choice === 'allow' ? 'Allow exception' : 'Respect default'; }
function resetDefaults() {
    if (!draft.value) return;
    for (const rule of rules) if (canEdit(rule.key)) draft.value[rule.key] = 'default';
}
function errorMessage(cause: unknown): string {
    const detail = cause as { data?: { message?: string }; statusMessage?: string; message?: string };
    return detail?.data?.message || detail?.statusMessage || detail?.message || 'Request failed. Please try again.';
}
async function loadPolicy() {
    loading.value = true;
    state.value = null;
    draft.value = null;
    error.value = '';
    reviewing.value = false;
    try {
        state.value = await $fetch<CampaignEligibilityPolicyState>(`/api/icps/${props.campaignId}/eligibility-policy`);
        draft.value = toDraft(state.value.policy);
        saveUncertain.value = false;
    } catch (cause) { error.value = errorMessage(cause); }
    finally { loading.value = false; }
}
async function openEditor() {
    open.value = true;
    notice.value = '';
    await loadPolicy();
}
function discoveryNotice(result: { status: string; noOp?: boolean }): string {
    return result.noOp ? 'There are already enough contacts ready for review. No new search was needed.'
        : 'Contact search requested. Follow progress in Review; contacts are not ready yet.';
}
async function savePolicy(action: 'save' | 'save-and-find') {
    if (!state.value || !draft.value || saving.value || saveUncertain.value) return;
    saving.value = true;
    error.value = '';
    notice.value = '';
    discoveryError.value = '';
    try {
        const result = await $fetch<SaveCampaignEligibilityPolicyResult>(`/api/icps/${props.campaignId}/eligibility-policy`, {
            method: 'POST', retry: 0,
            body: { confirmed: true, expectedConfigurationRevision: state.value.configurationRevision, action,
                policy: {
                    allowRecentCooldownContacts: draft.value.allowRecentCooldownContacts === 'allow',
                    allowManuallyExcludedCompanies: draft.value.allowManuallyExcludedCompanies === 'allow',
                    allowCrmDealCompanies: draft.value.allowCrmDealCompanies === 'allow',
                } },
        });
        state.value = result;
        draft.value = toDraft(result.policy);
        reviewing.value = false;
        notice.value = result.outcome === 'saved' ? 'Rules saved.' : 'Rules are unchanged.';
        if (result.discovery.status === 'failed') discoveryError.value = result.discovery.error;
        if (result.discovery.status === 'started') notice.value += ` ${discoveryNotice(result.discovery.processingState)}`;
    } catch (cause) {
        error.value = errorMessage(cause);
        saveUncertain.value = true;
    } finally { saving.value = false; }
}
async function retryDiscovery() {
    if (finding.value) return;
    finding.value = true;
    try {
        const result = await $fetch(`/api/icps/${props.campaignId}/resolve`, { method: 'POST', retry: 0, body: { reviewTargetCount: 50 } });
        discoveryError.value = '';
        notice.value = `Rules remain saved. ${discoveryNotice(result)}`;
    } catch (cause) { discoveryError.value = errorMessage(cause); }
    finally { finding.value = false; }
}
onMounted(() => { void loadPolicy(); });
watch(() => props.campaignId, () => { discoveryError.value = ''; notice.value = ''; void loadPolicy(); });
</script>
